import { OrgResponse, OrgId, ACHMethodRequest, OrgAddressResponse } from '@business/entities/organization';
import { BaseResponse } from '../common';
import { CompactUserResponse, VeryCompactUserResponse } from '../account';
import { ProposalFormData } from '@modules/proposal/ProposalBuilderPage';
import { CompanyResponse } from '../companies';
import { LineItemFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ProposalTemplateId } from './ProposalTemplateResponse';
import { ContactId } from '../contact';
import { DealIntervalResponse } from '../deal';
import { TMEstimateId, TMEstimateResponse, VeryCompactTMEstimateResponse } from '../tm';
import { EstimateItemFormData } from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import { PackageFormData } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';

export enum ProposalStatus {
  draft = 'draft',
  sent = 'sent',
  accepted = 'accepted',
  void = 'void',
  cancel = 'cancel',
  viewed = 'viewed',
  archived = 'archived',
  received = 'received',
}

export type ProposalId = string;
export type ProposalHistoryId = string;

export interface CompactProposalResponse extends BaseResponse {
  id: ProposalId;
  orgId: OrgId;
  estimateId: TMEstimateId;
  addressId: string;
  templateId: ProposalTemplateId;
  // Customer Design File selected in the proposal wizard's Design File step (optional).
  designFileId?: string | null;
  /** [templateId => hash] of the documents generated for the Terms & Conditions links */
  documentLinks?: Record<string, string>;
  /** Sent / viewed proposal past its validity (sent date + "Proposal expires after (days)"): can't be viewed, signed or paid */
  isExpired?: boolean;
  /** Unix seconds when a pending proposal expires; null when it can't expire (draft / accepted / archived / expiry off) */
  expiresAt?: number | null;
  // Resolved name/URL of that file — used by template links and the {{design_file_*}} short codes.
  designFile?: { id: string; name: string; url: string } | null;
  signees: string[];
  title: string;
  status: ProposalStatus;
  organization: OrgResponse;
  amount: number;
  deposit: number;
  createdBy: CompactUserResponse;
  createdAt: number;
  created: number;
  deletedAt?: number;
  image: string;
  deposited: boolean;
  depositAt?: number;
  company?: CompanyResponse;
  createDefaultModule?: ProposalDepositModule;
  salesReps: string[];
  boardId?: string;
  total?: number;
  isEditted?: boolean;
  dealContactId: ContactId;
  dealInterval: DealIntervalResponse;
  signedAt?: number;
  viewedAt?: number;
  senderEmails: string[];
  depositType: ProposalDiscountType;
  depositedFrom?: string;
  packages: PackageFormData[];
}

export type HistoryType = 'create' | 'update' | 'delete';
export interface ProposalHistoryResponse {
  id: ProposalHistoryId;
  message: string;
  type: HistoryType;
  createdAt: number;
}

export interface ProposalResponse extends CompactProposalResponse {
  previewUrl?: string;
  data?: ProposalFormData;
  estimate?: EstimateItemFormData;
  subProjects: VeryCompactTMEstimateResponse[];
  history: ProposalHistoryResponse[];
  senders: any[];
  signees: any[];
  deletedAt?: number;
  depositUrl?: string;
  selectedItems?: LineItemFormData[];
  signerLinks: { [key: string]: string };
  depositType: ProposalDiscountType;
  grandTotal?: number;
  subProjectIds: TMEstimateId[];
  listItemIds: string[];
  startDate?: number;
  weeksToComplete?: number;
  /** Who the customer should contact: the first sales person, else the creator */
  contactPerson?: ProposalContactPersonResponse | null;
}

export interface ProposalContactPersonResponse {
  name: string;
  email: string;
}

export interface VeryCompactProposalResponse {
  id: ProposalId;
  title: string;
  data?: ProposalFormData;
  senders: any[];
  organization: OrgResponse;
}

export type ResendProposalResponse = Pick<ProposalResponse, 'id' | 'title' | 'senders'>;

export type CompactProposalTableResponse = Pick<
  CompactProposalResponse,
  | 'id'
  | 'isExpired'
  | 'expiresAt'
  | 'estimateId'
  | 'title'
  | 'status'
  | 'created'
  | 'deletedAt'
  | 'orgId'
  | 'image'
  | 'deposit'
  | 'deposited'
  | 'depositAt'
  | 'createdBy'
  | 'amount'
  | 'templateId'
  | 'signees'
  | 'senderEmails'
  | 'salesReps'
  | 'isEditted'
  | 'boardId'
  | 'signedAt'
  | 'viewedAt'
  | 'depositType'
> & {
  signer?: any;
  history: ProposalHistoryResponse[];
  //   data: ProposalFormData;
  products: { title: string; price: string; selected: boolean; isOptional: boolean }[];
  previewUrl?: string;
  signerUrl?: { [key: string]: string };
};

export interface ProposalDepositAchRequest extends Omit<ACHMethodRequest, 'phone'> {
  save: boolean;
  amount: number;
  senderId?: string;
  senderEmail?: string;
  address?: Partial<OrgAddressResponse>;
}

export type ProposalDepositCardRequest = {
  name: string;
  cardNumber: string;
  expiryMonth: number | string;
  expiryYear: number | string;
  cvv: string;
  save: boolean;
  amount: number;
  senderId?: string;
  senderEmail?: string;
  address?: Partial<OrgAddressResponse>;
};

export type ProposalSigneeResponse = {
  depositUrl: string;
};

export enum ProposalDepositModule {
  project = 'project',
  workOrder = 'workOrder',
}

export const ProposalDepositModules = [ProposalDepositModule.project, ProposalDepositModule.workOrder];

export interface ProposalsPageQuery {
  orgId?: OrgId;
  search?: string;
}

export interface ExtendedLineItemFormData {
  labourTypeId: string;
  hour: number;
  cost: number;
  price: number;
  total: number;
}

export enum ProposalDiscountType {
  percentage = 'percentage',
  fixed = 'fixed',
}
export const ProposalDiscountLabel: Record<ProposalDiscountType, string> = {
  percentage: 'Deposit percent',
  fixed: 'Deposit value',
};

export enum ProposalDiscountCategory {
  labor = 'labor',
  items = 'items',
  itemsWithLabor = 'itemsWithLabor',
}

export const ProposalDiscountCategoryLabel: Record<ProposalDiscountCategory, string> = {
  labor: 'Labor',
  items: 'Items Only',
  itemsWithLabor: 'Items & Labor',
};

export interface ProposalRevisionResponse {
  id: ProposalId;
  orgId: OrgId;
  addressId: string;
  templateId: ProposalTemplateId;
  signees: string[];
  title: string;
  status: ProposalStatus;
  organization: OrgResponse;
  amount: number;
  deposit: number;
  createdBy: CompactUserResponse;
  createdAt: number;
  created: number;
  deletedAt?: number;
  image: string;
  deposited: boolean;
  depositAt?: number;
  company?: CompanyResponse;
  createDefaultModule?: ProposalDepositModule;
  salesReps: string[];
  singables: string[];
  boardId?: string;
  total?: number;
  isEditted?: boolean;
  dealContactId: ContactId;
  dealInterval: DealIntervalResponse;
  signedAt?: number;
  viewedAt?: number;
  senderEmails: string[];
  depositType: ProposalDiscountType;
  depositedFrom?: string;
  requestId?: string;
  canEdit: boolean;
  version: number;
  revisedAt: number;
  revisedBy: VeryCompactUserResponse;
  address?: OrgAddressResponse;
}
